'use client';

import { dockPositionView } from '@/lib/dock-position-check';
import { TONE_BADGE, TONE_TEXT } from '@/lib/dock-status';
import type { DockPositionCheck } from '@/types/dock';

/**
 * A dock's pin check, in the two shapes its surfaces need.
 *
 * - `panel` (COP dock panel): the badge, and the full sentence under it when there is something to
 *   act on - a drifted pin, or none.
 * - `table` (fleet docks table): a compact badge and, when actionable, one short visible hint - a row
 *   cannot afford the sentence, and a tooltip cannot be opened on a touch screen or reached by
 *   keyboard. An unchecked pin is a dim dash.
 *
 * No `title` tooltip on either: everything a sighted operator needs is visible, and the full sentence
 * is given to screen readers once, as text, rather than twice (text plus title).
 */
export function DockPositionBadge({
  check,
  variant = 'panel',
}: {
  check: DockPositionCheck | undefined;
  variant?: 'panel' | 'table';
}) {
  const view = dockPositionView(check);

  if (variant === 'table') {
    return (
      <div className="min-w-0">
        {view.compactLabel === null ? (
          <span data-testid="dock-position-badge" className="text-[11px] text-muted-foreground" aria-hidden="true">-</span>
        ) : (
          <span
            data-testid="dock-position-badge"
            aria-hidden="true"
            className={`inline-block whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] uppercase ${TONE_BADGE[view.tone]}`}
          >
            {view.compactLabel}
          </span>
        )}
        {view.hint && (
          <p data-testid="dock-position-hint" aria-hidden="true" className={`mt-0.5 whitespace-nowrap text-[11px] leading-tight ${TONE_TEXT[view.tone]}`}>
            {view.hint}
          </p>
        )}
        <span className="sr-only">{`${view.label}. ${view.detail}`}</span>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <span
        data-testid="dock-position-badge"
        className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-[10px] uppercase ${TONE_BADGE[view.tone]}`}
      >
        {view.label}
      </span>
      {view.actionable ? (
        <p data-testid="dock-position-detail" className={`mt-1 text-[11px] leading-snug ${TONE_TEXT[view.tone]}`}>
          {view.detail}
        </p>
      ) : (
        <span className="sr-only">{`. ${view.detail}`}</span>
      )}
    </div>
  );
}
